import { DeviceTypes } from '@eppendorf/app-configs';
import {
  DateRangePicker,
  QuickSelectOption,
  Switch,
} from '@eppendorf/vnls-react-components';
import { subDays, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useDeviceType } from '$components/app-config/useDeviceType';
import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';
import { ButtonToggle } from '$features/monitoring/history-charts-v2/components/button-toggle/button-toggle';
import { DeviceNotConfiguredForHistoryCharts } from '$features/monitoring/history-charts-v2/device-not-configured-for-charts';
import { HistoryChartV2Container } from '$features/monitoring/history-charts-v2/history-chart-v2-container';
import { useDateRangeChange } from '$features/monitoring/history-charts-v2/useDateRangeChange';
import { useHistoryChartsConfig } from '$features/monitoring/history-charts-v2/useHistoryChartsConfig';
import {
  RANGE_PERIOD,
  useRangePeriod,
} from '$features/monitoring/history-charts-v2/useRangePeriod';
import { useDeviceTelemetryDataForSnapshot } from '$features/monitoring/parameters/useDeviceTelemetryDataForSnapshot';
import { CreateReportButton } from '$features/monitoring/report';
import { canCreateReport } from '$features/monitoring/report/utils/can-create-report';
import { isCentrifugeLarge } from '$features/monitoring/shared/centrifuge-large.utils';
import { isCountdownTimerParameter } from '$features/monitoring/shared/countdown-timer.utils';
import {
  isThermomixer,
  THERMOMIXER_HIDDEN_CHART_PARAMS,
} from '$features/monitoring/shared/thermomixer.utils';
import {
  getMaxAllowedDays,
  useGetSubscription,
} from '$features/subscription/subscription-api';

/** Telemetry IDs to hide from history charts for centrifuge-large devices */
const CENTRIFUGE_LARGE_HIDDEN_CHART_PARAMS = new Set(['Runtime']);

export function HistoryChartsV2() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { data: snapshot } = useDeviceSnapshot({
    manufacturer: searchParams.get('manufacturer')!,
    serialNumber: searchParams.get('serialNumber')!,
  });
  const { data: subscription } = useGetSubscription();

  const deviceType = useDeviceType(snapshot?.inventoryDevice);
  const canCreate = canCreateReport(deviceType as DeviceTypes);

  const { primaryParameters: parameters } = useDeviceTelemetryDataForSnapshot(snapshot);

  const isCentrifugeLargeDevice = isCentrifugeLarge(snapshot?.inventoryDevice);
  const isThermomixerDevice = isThermomixer(snapshot?.inventoryDevice);

  // For centrifuge-large: filter out Runtime/Time chart
  // For thermomixer: filter out Time chart
  // For CS220: filter out Agitation Timer chart
  const chartParameters = useMemo(() => {
    const device = snapshot?.inventoryDevice;
    const visibleParameters = parameters.filter(
      (p) => !isCountdownTimerParameter(device, p.telemetryId),
    );
    if (isCentrifugeLargeDevice) {
      return visibleParameters.filter(
        (p) => !CENTRIFUGE_LARGE_HIDDEN_CHART_PARAMS.has(p.telemetryId),
      );
    }
    if (isThermomixerDevice) {
      return visibleParameters.filter(
        (p) => !THERMOMIXER_HIDDEN_CHART_PARAMS.has(p.telemetryId),
      );
    }
    return visibleParameters;
  }, [
    parameters,
    snapshot?.inventoryDevice,
    isCentrifugeLargeDevice,
    isThermomixerDevice,
  ]);

  const { showThresholdRefs, toggleShowThresholdRefs } = useHistoryChartsConfig();
  const { rangePeriod, rangePeriodItems, from, to } = useRangePeriod();
  const { onRangeChange } = useDateRangeChange();
  const [selectedQuickOption, setSelectedQuickOption] = useState<QuickSelectOption>();

  const handleDateRangeChange: typeof onRangeChange = (dateRange) => {
    onRangeChange(dateRange);
    setSelectedQuickOption(undefined);
  };

  const maxAllowedDays = getMaxAllowedDays(subscription);

  const disabledDates = {
    before: subDays(new Date(), maxAllowedDays),
    after: new Date(),
  };

  if (!chartParameters.length) {
    return (
      <div>
        <DeviceNotConfiguredForHistoryCharts />
      </div>
    );
  }

  return (
    <div>
      <FeatureIsEnabled feature="ESEVNLS_4482_STABILITY_REPORT">
        {canCreate && (
          <div className="m-bottom-l">
            <CreateReportButton
              serialNumber={searchParams.get('serialNumber') || ''}
              manufacturer={searchParams.get('manufacturer') || ''}
              initialDateRange={{
                from: from ? new Date(from) : undefined,
                to: to ? new Date(to) : undefined,
              }}
            />
          </div>
        )}
      </FeatureIsEnabled>
      <div className="flex flex__jc--center">
        <ButtonToggle items={rangePeriodItems} />
      </div>
      {rangePeriod === RANGE_PERIOD.CUSTOM && (
        <div className="m-bottom-xxl flex flex__dir--column">
          <DateRangePicker
            dateRange={{
              from: from ? new Date(from) : undefined,
              to: to ? new Date(to) : undefined,
            }}
            onDateRangeChange={handleDateRangeChange}
            quickSelect={{
              enabled: true,
              selectedOption: selectedQuickOption,
              onOptionSelect: (option) => {
                setSelectedQuickOption(option);
              },
            }}
            autoFocus
            defaultMonth={subMonths(disabledDates.after, 1)}
            disabled={disabledDates}
            startMonth={disabledDates.before}
            endMonth={disabledDates.after}
            className="m-bottom-m"
          />
        </div>
      )}
      <div className="m-bottom-m">
        <small className="font-size-s ">
          {t('monitoringDetail.historyChartsDetail.timezoneHint')}
        </small>
      </div>
      {/* Hide setpoint/threshold toggle for centrifuge-large devices */}
      {!isCentrifugeLargeDevice && (
        <div className="flex flex__dir--row flex__jc--flex-end gap-s m-x-m m-y-l">
          <span>
            {isThermomixerDevice
              ? t('monitoringDetail.historyChartsDetail.showSetpointToggleLabel')
              : t('monitoringDetail.historyChartsDetail.showAlertThresholdsToggleLabel')}
          </span>
          <Switch
            checked={showThresholdRefs}
            onCheckedChange={(state) => toggleShowThresholdRefs(state)}
          />
        </div>
      )}
      {snapshot && (
        <div>
          {chartParameters.map((parameter) => (
            <HistoryChartV2Container
              snapshot={snapshot}
              parameter={parameter}
              from={from}
              to={to}
              liveUpdate={rangePeriod === RANGE_PERIOD.LIVE}
              key={parameter.telemetryId}
              hideSetpoints={isCentrifugeLargeDevice}
              fractionalDigits={
                isCentrifugeLargeDevice || isThermomixerDevice ? 0 : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
